import { computePeriodRange, shiftRefDate } from "./periodRange.js";
import { fmtDE } from "../../app-input/utils/dateUtils.js";
import { createPeriodPopup, renderWeekPicker, renderMonthPicker, renderRangePicker } from "./periodPicker.js";

const PERIOD_MODES = ['week', 'month', 'year', 'custom'];

export class DateFilterManager {
    constructor(modeContainer, navContainer, onDateChange) {
        this._modeContainer = modeContainer;
        this._navContainer = navContainer;
        this._onDateChange = onDateChange;
        this._mode = 'month';
        this._refDate = new Date();
        this._custom = null;
        this._rangeFrom = null;
        this._rangeTo = null;
        this._buttons = [];
        this._slider = null;
        this._navEl = null;
        this._initializing = false;
        this._popup = createPeriodPopup(() => this._navContainer?.getRootNode?.());
    }

    init() {
        this._render();
    }

    setInitializing(value) {
        this._initializing = value;
        if (!value) this._emit();
    }

    get mode() { return this._mode; }

    destroy() {
        this._popup.close();
    }

    _render() {
        const modeContainer = this._modeContainer;
        const navContainer = this._navContainer;
        if (!modeContainer || !navContainer) return;
        modeContainer.innerHTML = `
            <div class="filter periodModeFilter">
                <div class="slider" id="periodModeSlider"></div>
                <button class="filterBtn" data-mode="week">Woche</button>
                <button class="filterBtn" data-mode="month">Monat</button>
                <button class="filterBtn" data-mode="year">Jahr</button>
                <button class="filterBtn" data-mode="custom">Zeitraum</button>
            </div>
        `;
        navContainer.innerHTML = '';
        navContainer.classList.add('dateControls');

        this._buttons = [...modeContainer.querySelectorAll('.periodModeFilter .filterBtn')];
        this._slider = modeContainer.querySelector('#periodModeSlider');
        this._navEl = navContainer;

        this._buttons.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.mode === this._mode);
            btn.addEventListener('click', () => {
                if (this._mode === btn.dataset.mode) return;
                this._mode = btn.dataset.mode;
                this._buttons.forEach(b => b.classList.toggle('active', b === btn));
                this._moveSlider();
                this._renderNav();
                requestAnimationFrame(() => this._emit());
            });
        });

        this._renderNav();
        requestAnimationFrame(() => this._moveSlider(false));
    }

    refreshLayout() {
        this._moveSlider(false);
    }

    _moveSlider(animate = true) {
        const activeBtn = this._buttons.find(b => b.classList.contains('active'));
        if (!this._slider || !activeBtn || !activeBtn.offsetWidth) return;
        this._slider.style.transition = animate ? 'transform 0.2s ease, width 0.2s ease' : 'none';
        this._slider.style.width = `${activeBtn.offsetWidth}px`;
        this._slider.style.transform = `translateX(${activeBtn.offsetLeft}px)`;
    }

    _renderNav() {
        const navEl = this._navEl;
        if (!navEl) return;

        this._popup.close();

        if (this._mode === 'custom') {
            const label = (this._custom?.from && this._custom?.to)
                ? `${fmtDE(this._custom.from)} – ${fmtDE(this._custom.to)}`
                : 'Zeitraum wählen';
            navEl.innerHTML = `<button class="periodLabel periodLabelCustom" id="periodLabelBtn" type="button">${label}</button>`;
            const labelBtn = navEl.querySelector('#periodLabelBtn');
            labelBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._popup.open(labelBtn, panel => renderRangePicker(panel, this._custom, this._refDate, (from, to) => {
                    this._custom = { from, to };
                    this._popup.close();
                    this._renderNav();
                    this._emit();
                }));
            });
            return;
        }

        const { from, to, label } = computePeriodRange(this._mode, this._refDate);
        this._rangeFrom = from;
        this._rangeTo = to;

        const pickable = this._mode === 'week' || this._mode === 'month';
        const labelHtml = pickable
            ? `<button class="periodLabel" id="periodLabelBtn" type="button">${label}</button>`
            : `<span class="periodLabel">${label}</span>`;
        navEl.innerHTML = `
            <button class="filterIconBtn" id="periodPrev" title="Vorheriger Zeitraum"><i class="fa-solid fa-chevron-left"></i></button>
            ${labelHtml}
            <button class="filterIconBtn" id="periodNext" title="Nächster Zeitraum"><i class="fa-solid fa-chevron-right"></i></button>
        `;
        navEl.querySelector('#periodPrev').addEventListener('click', () => {
            this._refDate = shiftRefDate(this._mode, this._refDate, -1);
            this._renderNav();
            this._emit();
        });
        navEl.querySelector('#periodNext').addEventListener('click', () => {
            this._refDate = shiftRefDate(this._mode, this._refDate, 1);
            this._renderNav();
            this._emit();
        });

        const labelBtn = navEl.querySelector('#periodLabelBtn');
        if (labelBtn) {
            labelBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                if (this._mode === 'week') {
                    this._popup.open(labelBtn, panel => renderWeekPicker(panel, this._refDate, (monday) => {
                        this._refDate = monday;
                        this._popup.close();
                        this._renderNav();
                        this._emit();
                    }));
                } else if (this._mode === 'month') {
                    this._popup.open(labelBtn, panel => renderMonthPicker(panel, this._refDate, (monthIndex) => {
                        this._refDate = new Date(this._refDate.getFullYear(), monthIndex, 1);
                        this._popup.close();
                        this._renderNav();
                        this._emit();
                    }));
                }
            });
        }
    }

    _emit() {
        if (this._initializing) return;

        if (this._mode === 'custom') {
            if (!this._custom?.from || !this._custom?.to) return;
            this._onDateChange({ mode: 'exact', from: this._custom.from, to: this._custom.to, period: 'custom' });
            return;
        }
        if (this._mode === 'month') {
            const [year, month] = this._rangeFrom.split('-');
            this._onDateChange({ mode: 'coarse', months: [month], year, from: this._rangeFrom, to: this._rangeTo, period: 'month' });
            return;
        }
        if (this._mode === 'year') {
            const year = this._rangeFrom.split('-')[0];
            this._onDateChange({ mode: 'coarse', months: [], year, from: this._rangeFrom, to: this._rangeTo, period: 'year' });
            return;
        }
        this._onDateChange({ mode: 'exact', from: this._rangeFrom, to: this._rangeTo, period: 'week' });
    }

    _selectMode(mode) {
        if (!PERIOD_MODES.includes(mode)) return;
        this._mode = mode;
        this._buttons.forEach(b => b.classList.toggle('active', b.dataset.mode === mode));
        this._moveSlider(false);
        this._renderNav();
    }

    restoreFromUrl({ period, year, month, from, to } = {}) {
        if (period === 'week') {
            if (!from) return;
            this._refDate = new Date(`${from}T00:00:00`);
            this._selectMode('week');
        } else if (period === 'month' || (!period && year && month)) {
            if (!year && !month) return;
            this._refDate = new Date(Number(year) || new Date().getFullYear(), month ? Number(month) - 1 : 0, 1);
            this._selectMode('month');
        } else if (period === 'year' || (!period && year && !month)) {
            if (!year) return;
            this._refDate = new Date(Number(year), 0, 1);
            this._selectMode('year');
        } else if (period === 'custom' || (!period && (from || to))) {
            if (!from && !to) return;
            this._custom = { from, to };
            this._selectMode('custom');
        } else {
            return;
        }
        this._emit();
    }
}
